import { useQuery } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useConnection } from 'hooks/useConnection'
import type { GetV2TokenPriceQueryVariables, GetV3TokenPriceQueryVariables } from 'queries/analytics/v3/generated'
import { useGetV2TokenPriceQuery, useGetV3TokenPriceQuery } from 'queries/analytics/v3/generated'
import { PriceChartEntry } from 'types/analytics'

dayjs.extend(utc)

type Version = 'v2' | 'v3'

const PAGE_SIZE = 200

const mapPriceEntries = (entries: Array<{ date: number; priceUSD?: string | null }>): PriceChartEntry[] => {
  return entries
    .slice()
    .sort((a, b) => a.date - b.date)
    .map((entry) => ({
      time: dayjs.unix(entry.date).utc().format('YYYY-MM-DD'),
      value: Number.parseFloat(entry.priceUSD ?? '0'),
    }))
    .filter((entry) => Number.isFinite(entry.value))
}

const fetchAllTokenPriceData = async (
  chainId: number,
  address: string,
  startTimestamp: number,
  version: Version
): Promise<PriceChartEntry[]> => {
  if (version === 'v3') {
    const { endpoint } = getSubgraphConfig(chainId, SubgraphType.V3)
    const fetcher = useGetV3TokenPriceQuery.fetcher

    let skip = 0
    const collected: PriceChartEntry[] = []
    let hasMore = true

    while (hasMore) {
      const variables: GetV3TokenPriceQueryVariables = {
        tokenAddress: address,
        startTime: startTimestamp,
        skip,
      }

      const result = await fetcher({ endpoint }, variables)()

      const page = mapPriceEntries(result.tokenDayDatas ?? [])
      collected.push(...page)

      if (!result.tokenDayDatas || result.tokenDayDatas.length < PAGE_SIZE) {
        hasMore = false
      } else {
        skip += PAGE_SIZE
      }
    }

    return collected
  }

  const { endpoint } = getSubgraphConfig(chainId, SubgraphType.V2)
  const fetcher = useGetV2TokenPriceQuery.fetcher
  let skip = 0
  const collected: PriceChartEntry[] = []
  let hasMore = true

  while (hasMore) {
    const variables: GetV2TokenPriceQueryVariables = {
      tokenAddress: address,
      startTime: startTimestamp,
      skip,
    }

    const result = await fetcher({ endpoint }, variables)()

    const page = mapPriceEntries(result.tokenDayDatas ?? [])
    collected.push(...page)

    if (!result.tokenDayDatas || result.tokenDayDatas.length < PAGE_SIZE) {
      hasMore = false
    } else {
      skip += PAGE_SIZE
    }
  }

  return collected
}

export function useTokenPriceData({
  address,
  startTimestamp,
  version,
  enabled = true,
}: {
  address?: string
  startTimestamp?: number
  version: Version
  enabled?: boolean
}) {
  const { chainId } = useConnection()

  return useQuery<PriceChartEntry[]>({
    queryKey: ['analytics', 'token-price', version, chainId, address ?? '', startTimestamp ?? 0],
    enabled: Boolean(enabled && address && startTimestamp),
    queryFn: () => {
      if (!address || !startTimestamp) {
        throw new Error('Address and start timestamp are required')
      }
      return fetchAllTokenPriceData(chainId, address.toLowerCase(), startTimestamp, version)
    },
  })
}
